import React, { useMemo, useState } from "react";
import { Tabs, Tab, Typography, Box, TextField, IconButton, Tooltip, Switch, FormControlLabel } from "@mui/material";
import "./AdminCategoriesPage.scss";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import GGLoaderButton from "../../../components/CommonComponents/GGLoaderButton/GGLoaderButton";
import SnackBar, { SnackTypes } from "../../../components/UtilityComponents/SnackBar/SnackBar";
import { CategoryAPIHandler } from "../../../services/NetworkClient/BackendAPIs/CategoryAPIHandler";
import { SubcategoryAPIHandler } from "../../../services/NetworkClient/BackendAPIs/SubcategoryAPIhandler";
import { Category } from "../../../Models/Category";
import { Subcategory } from "../../../Models/SubCategory";
import GGModalWindow from "../../../components/UtilityComponents/GGModalWindow/GGModalWindow";
import { Visibility, ContentCopy } from "@mui/icons-material";
import { UploadAPIHandler, UploadPurpose } from "../../../services/NetworkClient/BackendAPIs/UploadAPIHandler";
import { compressImage } from "../../../utils/FileCompressionUtils";

const AdminCategoriesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Categories state
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingCategoryById, setIsLoadingCategoryById] = useState(false);
  const [isLoadingCategoryBySlug, setIsLoadingCategoryBySlug] = useState(false);
  const [isLoadingCategorySave, setIsLoadingCategorySave] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [categoryFetchType, setCategoryFetchType] = useState<'all' | 'byId' | 'bySlug' | null>(null);
  const [categoryIdSearch, setCategoryIdSearch] = useState("");
  const [categorySlugSearch, setCategorySlugSearch] = useState("");
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // Subcategories state
  const [isLoadingSubcategories, setIsLoadingSubcategories] = useState(false);
  const [isLoadingSubcategoryById, setIsLoadingSubcategoryById] = useState(false);
  const [isLoadingSubcategorySave, setIsLoadingSubcategorySave] = useState(false);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [subcategorySearchQuery, setSubcategorySearchQuery] = useState("");
  const [editingSubcategoryId, setEditingSubcategoryId] = useState<string | null>(null);
  const [subcategoryFetchType, setSubcategoryFetchType] = useState<'all' | 'byId' | 'byCategoryId' | null>(null);
  const [subcategoryIdSearch, setSubcategoryIdSearch] = useState("");
  const [subcategoryCategoryIdSearch, setSubcategoryCategoryIdSearch] = useState("");
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // Category map state
  const [categoryMapData, setCategoryMapData] = useState<any[]>([]);
  const [isLoadingCategoryMap, setIsLoadingCategoryMap] = useState(false);

  // Form states
  const [categoryFormState, setCategoryFormState] = useState<any>({
    id: "",
    title: "",
    quote: "",
    iconImg: "",
    bannerImg: "",
    thumbImg: "",
    priority: false,
    slug: "",
  });

  const [subcategoryFormState, setSubcategoryFormState] = useState<any>({
    id: "",
    name: "",
    icon: "",
    categoryId: "",
    headerImageUrl: "",
    tagline: "",
  });

  // Snackbar
  const snackBarRef = React.useRef<{ showSnackbar(): any }>(null)
  const [snackBarParams, setSnackBarParams] = useState({ message: "", state: SnackTypes.success })
  const notify = (message: string, state: SnackTypes) => { setSnackBarParams({ message, state }); snackBarRef.current?.showSnackbar(); }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setActiveTab(newValue);

  // Image upload functions
  const handleImageUpload = async (file: File, fieldName: string, isSubcategory: boolean = false) => {
    if (!file) return;

    setUploadingImage(fieldName);
    setUploadProgress(0);

    try {
      const compressedFile = await compressImage(file, 1, 0.75);
      const session = await UploadAPIHandler.shared.createAdminSession({ purpose: isSubcategory ? UploadPurpose.ADMIN_SUBCATEGORY_IMAGE : UploadPurpose.ADMIN_CATEGORY_IMAGE, fileName: compressedFile.name, mimeType: compressedFile.type, sizeBytes: compressedFile.size });
      if (!session.singleUpload) throw new Error('Admin upload session has no signed request');
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(session.singleUpload!.method, session.singleUpload!.url);
        Object.entries(session.singleUpload!.headers).forEach(([name, value]) => xhr.setRequestHeader(name, value));
        xhr.upload.onprogress = (event) => { if (event.lengthComputable) setUploadProgress(Math.round((event.loaded / event.total) * 100)); };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed with ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(compressedFile);
      });
      const asset = await UploadAPIHandler.shared.completeAdmin(session.sessionId);

      if (asset.url) {
        if (isSubcategory) {
          setSubcategoryFormState((prev: any) => ({
            ...prev,
            [fieldName]: asset.url,
            [fieldName === 'icon' ? 'iconAssetId' : 'headerImageAssetId']: asset.assetId,
          }));
        } else {
          setCategoryFormState((prev: any) => ({
            ...prev,
            [fieldName]: asset.url,
            [`${fieldName}AssetId`]: asset.assetId,
          }));
        }
        notify(`Image uploaded successfully for ${fieldName}`, SnackTypes.success);
      }
    } catch (error) {
      notify(`Failed to upload image for ${fieldName}`, SnackTypes.error);
    } finally {
      setUploadingImage(null);
      setUploadProgress(0);
    }
  };

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify(`${fieldName} copied to clipboard`, SnackTypes.success);
    } catch (error) {
      notify(`Failed to copy ${fieldName}`, SnackTypes.error);
    }
  };

  const openImageInNewTab = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  // Category map function
  const fetchCategoryMap = async () => {
    setIsLoadingCategoryMap(true);
    try {
      const response = await CategoryAPIHandler.shared.getAllCategoriesWithSubcategories(1, 100);
      setCategoryMapData(response.categories);
      notify(`Loaded ${response.categories.length} categories with subcategories`, SnackTypes.success);
    } catch (e) {
      notify("Failed to load category map", SnackTypes.error);
    }
    setIsLoadingCategoryMap(false);
  };

  // Categories functions
  const fetchAllCategories = async () => {
    setIsLoadingCategories(true);
    try {
      const response = await CategoryAPIHandler.shared.getAllCategories(1, 100);
      setCategories(response.categories);
      setCategoryFetchType('all');
      notify(`Fetched ${response.categories.length} categories`, SnackTypes.success);
    } catch (e) {
      notify("Failed to load categories", SnackTypes.error);
    }
    setIsLoadingCategories(false);
  };

  const fetchCategoryById = async () => {
    if (!categoryIdSearch.trim()) {
      notify("Please enter a category ID", SnackTypes.error);
      return;
    }
    setIsLoadingCategoryById(true);
    try {
      const category = await CategoryAPIHandler.shared.getCategoryById(categoryIdSearch.trim());
      setCategories([category]);
      setCategoryFetchType('byId');
      notify(`Found category: ${category.title}`, SnackTypes.success);
    } catch (e) {
      notify(`Category with ID "${categoryIdSearch}" not found`, SnackTypes.error);
      setCategories([]);
      setCategoryFetchType('byId');
    }
    setIsLoadingCategoryById(false);
  };

  const fetchCategoryBySlug = async () => {
    if (!categorySlugSearch.trim()) {
      notify("Please enter a category slug", SnackTypes.error);
      return;
    }
    setIsLoadingCategoryBySlug(true);
    try {
      const category = await CategoryAPIHandler.shared.getCategoryBySlug(categorySlugSearch.trim());
      setCategories([category]);
      setCategoryFetchType('bySlug');
      notify(`Found category: ${category.title}`, SnackTypes.success);
    } catch (e) {
      notify(`Category with slug "${categorySlugSearch}" not found`, SnackTypes.error);
      setCategories([]);
      setCategoryFetchType('bySlug');
    }
    setIsLoadingCategoryBySlug(false);
  };

  const clearCategoryList = () => {
    setCategories([]);
    setCategoryFetchType(null);
    setCategorySearchQuery("");
    setCategoryIdSearch("");
    setCategorySlugSearch("");
    setEditingCategoryId(null);
    notify("Category list cleared", SnackTypes.success);
  };

  // Subcategories functions
  const fetchAllSubcategories = async () => {
    setIsLoadingSubcategories(true);
    try {
      const response = await SubcategoryAPIHandler.shared.getAllSubcategories(1, 100);
      setSubcategories(response.subcategories);
      setSubcategoryFetchType('all');
      notify(`Fetched ${response.subcategories.length} subcategories`, SnackTypes.success);
    } catch (e) {
      notify("Failed to load subcategories", SnackTypes.error);
    }
    setIsLoadingSubcategories(false);
  };

  const fetchSubcategoryById = async () => {
    if (!subcategoryIdSearch.trim()) {
      notify("Please enter a subcategory ID", SnackTypes.error);
      return;
    }
    setIsLoadingSubcategoryById(true);
    try {
      const subcategory = await SubcategoryAPIHandler.shared.getSubcategoryById(subcategoryIdSearch.trim());
      setSubcategories([subcategory]);
      setSubcategoryFetchType('byId');
      notify(`Found subcategory: ${subcategory.name}`, SnackTypes.success);
    } catch (e) {
      notify(`Subcategory with ID "${subcategoryIdSearch}" not found`, SnackTypes.error);
      setSubcategories([]);
      setSubcategoryFetchType('byId');
    }
    setIsLoadingSubcategoryById(false);
  };

  const fetchSubcategoryByCategoryId = async () => {
    if (!subcategoryCategoryIdSearch.trim()) {
      notify("Please enter a subcategory category ID", SnackTypes.error);
      return;
    }
    setIsLoadingSubcategoryById(true);
    try {
      const subcategories = await SubcategoryAPIHandler.shared.getSubcategoriesForCategory(subcategoryCategoryIdSearch.trim());
      setSubcategories(subcategories.subcategories);
      setSubcategoryFetchType('byCategoryId');
      notify(`Found subcategories for category ID: ${subcategoryCategoryIdSearch}`, SnackTypes.success);
    } catch (e) {
      notify(`Subcategories with category ID "${subcategoryCategoryIdSearch}" not found`, SnackTypes.error);
      setSubcategories([]);
      setSubcategoryFetchType('byId');
    }
    setIsLoadingSubcategoryById(false);
  };

  

  const clearSubcategoryList = () => {
    setSubcategories([]);
    setSubcategoryFetchType(null);
    setSubcategorySearchQuery("");
    setSubcategoryIdSearch("");
    setSubcategoryCategoryIdSearch("");
    setEditingSubcategoryId(null);
    notify("Subcategory list cleared", SnackTypes.success);
  };

  // Filter functions
  const filteredCategories = useMemo(() => {
    const q = categorySearchQuery.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter(c =>
      c.title.toLowerCase().includes(q) ||
      c.slug.toLowerCase().includes(q) ||
      c.id.toLowerCase().includes(q)
    );
  }, [categorySearchQuery, categories]);

  const filteredSubcategories = useMemo(() => {
    const q = subcategorySearchQuery.trim().toLowerCase();
    if (!q) return subcategories;
    return subcategories.filter(s =>
      s.name.toLowerCase().includes(q) ||
      s.id.toLowerCase().includes(q) ||
      s.categoryId.toLowerCase().includes(q)
    );
  }, [subcategorySearchQuery, subcategories]);

  // Form functions
  const resetCategoryForm = () => setCategoryFormState({
    id: "",
    title: "",
    quote: "",
    iconImg: "",
    bannerImg: "",
    thumbImg: "",
    priority: false,
    slug: "",
  });

  const resetSubcategoryForm = () => setSubcategoryFormState({
    id: "",
    name: "",
    icon: "",
    categoryId: "",
    headerImageUrl: "",
    tagline: "",
  });

  const startCreateCategory = () => {
    resetCategoryForm();
    setEditingCategoryId("new");
    setShowCategoryModal(true);
  };

  const startEditCategory = (c: Category) => {
    setCategoryFormState({
      id: c.id,
      title: c.title,
      quote: c.quote,
      iconImg: c.iconImg,
      bannerImg: c.bannerImg,
      thumbImg: c.thumbImg,
      priority: c.priority,
      slug: c.slug,
    });
    setEditingCategoryId(c.id);
    setShowCategoryModal(true);
  };

  const startCreateSubcategory = () => {
    resetSubcategoryForm();
    setEditingSubcategoryId("new");
    setShowSubcategoryModal(true);
  };

  const startEditSubcategory = (s: Subcategory) => {
    setSubcategoryFormState({
      id: s.id,
      name: s.name,
      icon: s.icon,
      categoryId: s.categoryId,
      headerImageUrl: s.headerImageUrl,
      tagline: s.tagline,
    });
    setEditingSubcategoryId(s.id);
    setShowSubcategoryModal(true);
  };

  const cancelCategoryEdit = () => {
    setEditingCategoryId(null);
    resetCategoryForm();
    setShowCategoryModal(false);
  };

  const cancelSubcategoryEdit = () => {
    setEditingSubcategoryId(null);
    resetSubcategoryForm();
    setShowSubcategoryModal(false);
  };

  const saveCategory = async () => {
    setIsLoadingCategorySave(true);
    try {
      if (editingCategoryId === "new") {
        const created = await CategoryAPIHandler.shared.postNewCategory(categoryFormState);
        setCategories(prev => [created, ...prev]);
      } else if (editingCategoryId) {
        const updated = await CategoryAPIHandler.shared.updateCategoryById(categoryFormState, editingCategoryId);
        setCategories(prev => prev.map(c => c.id === editingCategoryId ? updated : c));
      }
      notify("Category saved successfully", SnackTypes.success);
      cancelCategoryEdit();
    } catch (e) {
      notify("Failed to save category", SnackTypes.error);
    }
    setIsLoadingCategorySave(false);
  };

  const saveSubcategory = async () => {
    setIsLoadingSubcategorySave(true);
    try {
      if (editingSubcategoryId === "new") {
        const created = await SubcategoryAPIHandler.shared.postNewSubcategory(subcategoryFormState.categoryId, subcategoryFormState);
        setSubcategories(prev => [created, ...prev]);
      } else if (editingSubcategoryId) {
        const updated = await SubcategoryAPIHandler.shared.updateSubcategoryById(subcategoryFormState, editingSubcategoryId);
        setSubcategories(prev => prev.map(s => s.id === editingSubcategoryId ? updated : s));
      }
      notify("Subcategory saved successfully", SnackTypes.success);
      cancelSubcategoryEdit();
    } catch (e) {
      notify("Failed to save subcategory", SnackTypes.error);
    }
    setIsLoadingSubcategorySave(false);
  };

  const deleteCategory = async (id: string) => {
    try {
      await CategoryAPIHandler.shared.deleteCategoryById(id);
      setCategories(prev => prev.filter(c => c.id !== id));
      notify("Category deleted successfully", SnackTypes.success);
    } catch (e) {
      notify("Failed to delete category", SnackTypes.error);
    }
  };

  const deleteSubcategory = async (id: string) => {
    try {
      await SubcategoryAPIHandler.shared.deleteSubcategoryById(id);
      setSubcategories(prev => prev.filter(s => s.id !== id));
      notify("Subcategory deleted successfully", SnackTypes.success);
    } catch (e) {
      notify("Failed to delete subcategory", SnackTypes.error);
    }
  };

  return (
    <div className="admin-categories-page">
      <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={4000} message={snackBarParams.message} />

      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        color: 'white',
        p: 2,
        borderRadius: 2,
        mb: 1,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
          Categories & Subcategories Dashboard
        </Typography>
      </Box>

      {/* Main Content Card */}
      <Box sx={{
        background: 'white',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.05)'
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            background: '#f8fafc',
            px: 3,
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              height: 3,
              borderRadius: '2px 2px 0 0'
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#64748b',
              '&.Mui-selected': {
                color: '#1e293b'
              }
            }
          }}
        >
          <Tab label="📊 Stats" />
          <Tab label="📁 Manage Categories" />
          <Tab label="📂 Manage Subcategories" />
          <Tab label="🗺️ Visualize Category Map" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {/* Stats Tab */}
          {activeTab === 0 && (
            <Box sx={{
              textAlign: 'center',
              p: 4,
              color: '#64748b',
              background: '#f8fafc',
              borderRadius: 2,
              border: '1px solid #e2e8f0'
            }}>
              <Typography variant="h6" sx={{ mb: 1, opacity: 0.5 }}>📊</Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>Stats Coming Soon</Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>Category and subcategory statistics will be displayed here</Typography>
            </Box>
          )}

          {/* Manage Categories Tab */}
          {activeTab === 1 && (
            <Box>
              {/* Fetch Controls */}
              <Box sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                p: 2,
                background: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0'
              }}>
                <GGLoaderButton
                  isLoading={isLoadingCategories}
                  onClick={fetchAllCategories}
                  variant="contained"
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                    }
                  }}
                >
                  📋 All Categories
                </GGLoaderButton>

                <GGLoaderButton
                  onClick={startCreateCategory}
                  variant="contained"
                  isLoading={false}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'
                    }
                  }}
                >
                  <AddIcon sx={{ mr: 0.5 }} />New Category
                </GGLoaderButton>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
                  <TextField
                    size="small"
                    placeholder="Search by ID"
                    value={categoryIdSearch}
                    onChange={(e) => setCategoryIdSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && fetchCategoryById()}
                    sx={{ minWidth: '150px' }}
                  />
                  <IconButton
                    onClick={fetchCategoryById}
                    disabled={!categoryIdSearch.trim()}
                    sx={{
                      background: '#3b82f6',
                      color: 'white',
                      '&:hover': {
                        background: '#2563eb'
                      },
                      '&:disabled': {
                        background: '#e2e8f0',
                        color: '#94a3b8'
                      }
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="Search by slug"
                    value={categorySlugSearch}
                    onChange={(e) => setCategorySlugSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && fetchCategoryBySlug()}
                    sx={{ minWidth: '150px' }}
                  />
                  <IconButton
                    onClick={fetchCategoryBySlug}
                    disabled={!categorySlugSearch.trim()}
                    sx={{
                      background: '#10b981',
                      color: 'white',
                      '&:hover': {
                        background: '#059669'
                      },
                      '&:disabled': {
                        background: '#e2e8f0',
                        color: '#94a3b8'
                      }
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Status Panel */}
              {categoryFetchType && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  background: categoryFetchType === 'all' ? '#e3f2fd' : categoryFetchType === 'byId' ? '#f3e8ff' : '#e8f5e8',
                  border: `1px solid ${categoryFetchType === 'all' ? '#2196f3' : categoryFetchType === 'byId' ? '#8b5cf6' : '#4caf50'}`,
                  minHeight: '40px'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    {categoryFetchType === 'all' ? '📋 All Categories' :
                      categoryFetchType === 'byId' ? `🔍 Search Result by ID: "${categoryIdSearch}"` :
                        `🔍 Search Result by Slug: "${categorySlugSearch}"`} • {categories.length} items
                  </Typography>

                  <GGLoaderButton
                    variant="outlined"
                    onClick={clearCategoryList}
                    isLoading={false}
                    size="small"
                    sx={{ color: '#64748b', borderColor: '#d1d5db' }}
                  >
                    Clear
                  </GGLoaderButton>
                </Box>
              )}

              {/* Search Filter */}
              {categories.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    placeholder="Filter current results..."
                    value={categorySearchQuery}
                    onChange={(e) => setCategorySearchQuery(e.target.value)}
                    sx={{ width: '300px' }}
                  />
                </Box>
              )}

              {/* Categories List */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 2 }}>
                {(isLoadingCategories || isLoadingCategoryById || isLoadingCategoryBySlug) && categories.length === 0 ? (
                  <Box sx={{
                    textAlign: 'center',
                    p: 4,
                    color: '#64748b',
                    fontSize: '1.1rem',
                    background: '#f8fafc',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0'
                  }}>
                    Loading categories...
                  </Box>
                ) : filteredCategories.length === 0 ? (
                  <Box sx={{
                    textAlign: 'center',
                    p: 4,
                    color: '#64748b',
                    background: '#f8fafc',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0'
                  }}>
                    <Typography variant="h6" sx={{ mb: 1, opacity: 0.5 }}>📁</Typography>
                    <Typography variant="h6" sx={{ mb: 1 }}>No categories found</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>Try fetching categories or create a new one</Typography>
                  </Box>
                ) : (
                  filteredCategories.map(c => (
                    <Box key={c.id} sx={{
                      borderLeft: '2px solid',
                      borderColor: categoryFetchType === 'all' ? '#3b82f6' : categoryFetchType === 'byId' ? '#8b5cf6' : '#4caf50',
                      borderRadius: 3,
                      p: 2,
                      background: 'white',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                        transform: 'translateY(-4px)'
                      }
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1, pr: 2 }}>
                          <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#1e293b' }}>{c.title}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                            {c.quote || 'No quote provided'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit Category">
                            <IconButton
                              size="small"
                              onClick={() => startEditCategory(c)}
                              sx={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                '&:hover': {
                                  background: '#e3f2fd',
                                  borderColor: '#3b82f6'
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Category">
                            <IconButton
                              size="small"
                              onClick={() => deleteCategory(c.id)}
                              sx={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                '&:hover': {
                                  background: '#ffebee',
                                  borderColor: '#ef4444'
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: 1,
                        mt: 2,
                        p: 1,
                        background: '#f8fafc',
                        borderRadius: 2,
                        border: '1px solid #e2e8f0'
                      }}>
                        <DataRow label="ID" value={c.id} onCopy={copyToClipboard} />
                        <DataRow label="Slug" value={c.slug} onCopy={copyToClipboard} />
                        <DataRow label="Priority" value={c.priority ? 'Yes' : 'No'} onCopy={copyToClipboard} />
                        <DataRow label="Icon" value={c.iconImg} isUrl={!!c.iconImg} onCopy={copyToClipboard} onView={openImageInNewTab} />
                        <DataRow label="Banner" value={c.bannerImg} isUrl={!!c.bannerImg} onCopy={copyToClipboard} onView={openImageInNewTab} />
                        <DataRow label="Thumb" value={c.thumbImg} isUrl={!!c.thumbImg} onCopy={copyToClipboard} onView={openImageInNewTab} />
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          )}

          {/* Manage Subcategories Tab */}
          {activeTab === 2 && (
            <Box>
              {/* Fetch Controls */}
              <Box sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                p: 2,
                background: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0'
              }}>
                <GGLoaderButton
                  isLoading={isLoadingSubcategories}
                  onClick={fetchAllSubcategories}
                  variant="contained"
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                    }
                  }}
                >
                  📂 All Subcategories
                </GGLoaderButton>

                <GGLoaderButton
                  onClick={startCreateSubcategory}
                  variant="contained"
                  isLoading={false}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'
                    }
                  }}
                >
                  <AddIcon sx={{ mr: 0.5 }} />New Subcategory
                </GGLoaderButton>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
                  <TextField
                    size="small"
                    placeholder="Search by ID"
                    value={subcategoryIdSearch}
                    onChange={(e) => setSubcategoryIdSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && fetchSubcategoryById()}
                    sx={{ minWidth: '150px' }}
                  />
                  <IconButton
                    onClick={fetchSubcategoryById}
                    disabled={!subcategoryIdSearch.trim()}
                    sx={{
                      background: '#3b82f6',
                      color: 'white',
                      '&:hover': {
                        background: '#2563eb'
                      },
                      '&:disabled': {
                        background: '#e2e8f0',
                        color: '#94a3b8'
                      }
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="Search by category ID"
                    value={subcategoryCategoryIdSearch}
                    onChange={(e) => setSubcategoryCategoryIdSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && fetchSubcategoryByCategoryId()}
                    sx={{ minWidth: '150px' }}
                  />
                  <IconButton
                    onClick={fetchSubcategoryByCategoryId}
                    disabled={!subcategoryCategoryIdSearch.trim()}
                    sx={{
                      background: '#3b82f6',
                      color: 'white',
                      '&:hover': {
                        background: '#2563eb'
                      },
                      '&:disabled': {
                        background: '#e2e8f0',
                        color: '#94a3b8'
                      }
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </Box>

              </Box>

              {/* Status Panel */}
              {subcategoryFetchType && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  background: subcategoryFetchType === 'all' ? '#e3f2fd' : '#f3e8ff',
                  border: `1px solid ${subcategoryFetchType === 'all' ? '#2196f3' : '#8b5cf6'}`,
                  minHeight: '40px'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    {subcategoryFetchType === 'all' ? '📂 All Subcategories' :
                      subcategoryFetchType === 'byId' ? `🔍 Search Result by ID: "${subcategoryIdSearch}"` : 
                      `🔍 Search Result by category ID: "${subcategoryCategoryIdSearch}"`} • {subcategories.length} items
                  </Typography>

                  <GGLoaderButton
                    variant="outlined"
                    onClick={clearSubcategoryList}
                    isLoading={false}
                    size="small"
                    sx={{ color: '#64748b', borderColor: '#d1d5db' }}
                  >
                    Clear
                  </GGLoaderButton>
                </Box>
              )}

              {/* Search Filter */}
              {subcategories.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    placeholder="Filter current results..."
                    value={subcategorySearchQuery}
                    onChange={(e) => setSubcategorySearchQuery(e.target.value)}
                    sx={{ width: '300px' }}
                  />
                </Box>
              )}

              {/* Subcategories List */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 2 }}>
                {(isLoadingSubcategories || isLoadingSubcategoryById) && subcategories.length === 0 ? (
                  <Box sx={{
                    textAlign: 'center',
                    p: 4,
                    color: '#64748b',
                    fontSize: '1.1rem',
                    background: '#f8fafc',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0'
                  }}>
                    Loading subcategories...
                  </Box>
                ) : filteredSubcategories.length === 0 ? (
                  <Box sx={{
                    textAlign: 'center',
                    p: 4,
                    color: '#64748b',
                    background: '#f8fafc',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0'
                  }}>
                    <Typography variant="h6" sx={{ mb: 1, opacity: 0.5 }}>📂</Typography>
                    <Typography variant="h6" sx={{ mb: 1 }}>No subcategories found</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>Try fetching subcategories or create a new one</Typography>
                  </Box>
                ) : (
                  filteredSubcategories.map(s => (
                    <Box key={s.id} sx={{
                      borderLeft: '2px solid',
                      borderColor: subcategoryFetchType === 'all' ? '#3b82f6' : '#8b5cf6',
                      borderRadius: 3,
                      p: 2,
                      background: 'white',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                        transform: 'translateY(-4px)'
                      }
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1, pr: 2 }}>
                          <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#1e293b' }}>{s.name}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                            {s.tagline || 'No tagline provided'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit Subcategory">
                            <IconButton
                              size="small"
                              onClick={() => startEditSubcategory(s)}
                              sx={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                '&:hover': {
                                  background: '#e3f2fd',
                                  borderColor: '#3b82f6'
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Subcategory">
                            <IconButton
                              size="small"
                              onClick={() => deleteSubcategory(s.id)}
                              sx={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                '&:hover': {
                                  background: '#ffebee',
                                  borderColor: '#ef4444'
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: 1,
                        mt: 2,
                        p: 1,
                        background: '#f8fafc',
                        borderRadius: 2,
                        border: '1px solid #e2e8f0'
                      }}>
                        <DataRow label="ID" value={s.id} onCopy={copyToClipboard} />
                        <DataRow label="Category ID" value={s.categoryId} onCopy={copyToClipboard} />
                        <DataRow label="Icon" value={s.icon} isUrl={!!s.icon} onCopy={copyToClipboard} onView={openImageInNewTab} />
                        <DataRow label="Header Image" value={s.headerImageUrl} isUrl={!!s.headerImageUrl} onCopy={copyToClipboard} onView={openImageInNewTab} />
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          )}

          {/* Visualize Category Map Tab */}
          {activeTab === 3 && (
            <Box>
              {/* Fetch Controls */}
              <Box sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                p: 2,
                background: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0'
              }}>
                <GGLoaderButton
                  isLoading={isLoadingCategoryMap}
                  onClick={fetchCategoryMap}
                  variant="contained"
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                    }
                  }}
                >
                  🗺️ Load Category Map
                </GGLoaderButton>
              </Box>

              {/* Category Map Visualization */}
              {categoryMapData.length > 0 ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(800px, 1fr))', gap: 2 }}>
                  {categoryMapData.map((categoryData) => (
                    <Box key={categoryData.category.id} sx={{
                      borderLeft: '4px solid #3b82f6',
                      borderRadius: 3,
                      p: 3,
                      background: 'white',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                        transform: 'translateY(-4px)'
                      }
                    }}>
                      {/* Category Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1, pr: 2 }}>
                          <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#1e293b' }}>
                            {categoryData.category.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                            {categoryData.category.quote || 'No quote provided'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Copy Category ID">
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(categoryData.category.id, 'Category ID')}
                              sx={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                '&:hover': {
                                  background: '#e3f2fd',
                                  borderColor: '#3b82f6'
                                }
                              }}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Category Details */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: 1,
                        mb: 2,
                        p: 1,
                        background: '#f8fafc',
                        borderRadius: 2,
                        border: '1px solid #e2e8f0'
                      }}>
                        <DataRow label="ID" value={categoryData.category.id} onCopy={copyToClipboard} />
                        <DataRow label="Slug" value={categoryData.category.slug} onCopy={copyToClipboard} />
                        <DataRow label="Priority" value={categoryData.category.priority ? 'Yes' : 'No'} onCopy={copyToClipboard} />
                      </Box>

                      {/* Subcategories Section */}
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#1e293b' }}>
                          Subcategories ({categoryData.subcategories.length})
                        </Typography>

                        {categoryData.subcategories.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {categoryData.subcategories.map((subcategory: any) => (
                              <Box key={subcategory.id} sx={{
                                p: 2,
                                background: '#f8fafc',
                                borderRadius: 2,
                                border: '1px solid #e2e8f0',
                                borderLeft: '3px solid #10b981'
                              }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b' }}>
                                      {subcategory.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {subcategory.tagline || 'No tagline provided'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="Copy Subcategory ID">
                                      <IconButton
                                        size="small"
                                        onClick={() => copyToClipboard(subcategory.id, 'Subcategory ID')}
                                        sx={{
                                          padding: '2px',
                                          '&:hover': { background: '#e3f2fd' }
                                        }}
                                      >
                                        <ContentCopy fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {subcategory.icon && (
                                      <Tooltip title="View Icon">
                                        <IconButton
                                          size="small"
                                          onClick={() => openImageInNewTab(subcategory.icon)}
                                          sx={{
                                            padding: '2px',
                                            '&:hover': { background: '#e8f5e8' }
                                          }}
                                        >
                                          <Visibility fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </Box>
                                <Box sx={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                                  gap: 0.5,
                                  mt: 1
                                }}>
                                  <DataRow label="ID" value={subcategory.id} onCopy={copyToClipboard} />
                                  <DataRow label="Icon" value={subcategory.icon} isUrl={!!subcategory.icon} onCopy={copyToClipboard} onView={openImageInNewTab} />
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{
                            textAlign: 'center',
                            p: 2,
                            color: '#64748b',
                            background: '#f8fafc',
                            borderRadius: 2,
                            border: '1px solid #e2e8f0'
                          }}>
                            <Typography variant="body2" sx={{ opacity: 0.7 }}>No subcategories found</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{
                  textAlign: 'center',
                  p: 4,
                  color: '#64748b',
                  background: '#f8fafc',
                  borderRadius: 2,
                  border: '1px solid #e2e8f0'
                }}>
                  <Typography variant="h6" sx={{ mb: 1, opacity: 0.5 }}>🗺️</Typography>
                  <Typography variant="h6" sx={{ mb: 1 }}>No Category Map Loaded</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>Click "Load Category Map" to visualize categories and their subcategories</Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Category Modal */}
      <GGModalWindow
        childView={
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#1e293b' }}>
                {editingCategoryId === 'new' ? 'Create Category' : `Edit Category: ${categoryFormState.title}`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <GGLoaderButton
                  isLoading={isLoadingCategorySave}
                  onClick={saveCategory}
                  variant="contained"
                  size="small"
                  colorHex="#228B22"
                >
                  <SaveIcon sx={{ mr: 0.5 }} />Save
                </GGLoaderButton>
                <GGLoaderButton
                  variant="contained"
                  onClick={cancelCategoryEdit}
                  isLoading={false}
                  size="small"
                  colorHex="#8B0000"
                >
                  <CloseIcon sx={{ mr: 0.5 }} />Cancel
                </GGLoaderButton>
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 2 }}>
              <TextField
                label="Title"
                value={categoryFormState.title}
                onChange={e => setCategoryFormState((s: any) => ({ ...s, title: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Quote"
                value={categoryFormState.quote}
                onChange={e => setCategoryFormState((s: any) => ({ ...s, quote: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={categoryFormState.priority}
                    onChange={(e) => setCategoryFormState((s: any) => ({ ...s, priority: e.target.checked }))}
                  />
                }
                label="Priority"
              />

              {/* Icon Image Upload */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>Icon Image</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file, 'iconImg', false);
                    }}
                    id="icon-upload"
                  />
                  {categoryFormState.iconImg && (
                    <IconButton
                      size="small"
                      onClick={() => openImageInNewTab(categoryFormState.iconImg)}
                      sx={{ '&:hover': { background: '#e8f5e8' } }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {categoryFormState.iconImg && (
                  <TextField
                    label="Icon Image URL"
                    value={categoryFormState.iconImg}
                    onChange={e => setCategoryFormState((s: any) => ({ ...s, iconImg: e.target.value }))}
                    fullWidth
                    size="small"
                  />
                )}
                {uploadingImage === 'iconImg' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption">Uploading... {uploadProgress}%</Typography>
                    <Box sx={{ flex: 1, height: 4, background: '#e2e8f0', borderRadius: 2 }}>
                      <Box sx={{ height: '100%', background: '#3b82f6', borderRadius: 2, width: `${uploadProgress}%` }} />
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Banner Image Upload */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>Banner Image</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file, 'bannerImg', false);
                    }}
                    id="banner-upload"
                  />
                  {categoryFormState.bannerImg && (
                    <IconButton
                      size="small"
                      onClick={() => openImageInNewTab(categoryFormState.bannerImg)}
                      sx={{ '&:hover': { background: '#e8f5e8' } }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {categoryFormState.bannerImg && (
                  <TextField
                    label="Banner Image URL"
                    value={categoryFormState.bannerImg}
                    onChange={e => setCategoryFormState((s: any) => ({ ...s, bannerImg: e.target.value }))}
                    fullWidth
                    size="small"
                  />
                )}
                {uploadingImage === 'bannerImg' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption">Uploading... {uploadProgress}%</Typography>
                    <Box sx={{ flex: 1, height: 4, background: '#e2e8f0', borderRadius: 2 }}>
                      <Box sx={{ height: '100%', background: '#3b82f6', borderRadius: 2, width: `${uploadProgress}%` }} />
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Thumbnail Image Upload */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>Thumbnail Image</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file, 'thumbImg', false);
                    }}
                    id="thumb-upload"
                  />
                  
                  {categoryFormState.thumbImg && (
                    <IconButton
                      size="small"
                      onClick={() => openImageInNewTab(categoryFormState.thumbImg)}
                      sx={{ '&:hover': { background: '#e8f5e8' } }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {categoryFormState.thumbImg && (
                  <TextField
                    label="Thumbnail Image URL"
                    value={categoryFormState.thumbImg}
                    onChange={e => setCategoryFormState((s: any) => ({ ...s, thumbImg: e.target.value }))}
                    fullWidth
                    size="small"
                  />
                )}
                {uploadingImage === 'thumbImg' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption">Uploading... {uploadProgress}%</Typography>
                    <Box sx={{ flex: 1, height: 4, background: '#e2e8f0', borderRadius: 2 }}>
                      <Box sx={{ height: '100%', background: '#3b82f6', borderRadius: 2, width: `${uploadProgress}%` }} />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        }
        open={showCategoryModal}
        onCloseHandler={cancelCategoryEdit}
        title="Category Form"
        width="70%"
      />

      {/* Subcategory Modal */}
      <GGModalWindow
        childView={
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#1e293b' }}>
                {editingSubcategoryId === 'new' ? 'Create Subcategory' : `Edit Subcategory: ${subcategoryFormState.name}`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <GGLoaderButton
                  isLoading={isLoadingSubcategorySave}
                  onClick={saveSubcategory}
                  variant="contained"
                  size="small"
                  colorHex="#228B22"
                >
                  <SaveIcon sx={{ mr: 0.5 }} />Save
                </GGLoaderButton>
                <GGLoaderButton
                  variant="contained"
                  onClick={cancelSubcategoryEdit}
                  isLoading={false}
                  size="small"
                  colorHex="#8B0000"
                >
                  <CloseIcon sx={{ mr: 0.5 }} />Cancel
                </GGLoaderButton>
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 2 }}>
              <TextField
                label="Name"
                value={subcategoryFormState.name}
                onChange={e => setSubcategoryFormState((s: any) => ({ ...s, name: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Category ID"
                value={subcategoryFormState.categoryId}
                onChange={e => setSubcategoryFormState((s: any) => ({ ...s, categoryId: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Tagline"
                value={subcategoryFormState.tagline}
                onChange={e => setSubcategoryFormState((s: any) => ({ ...s, tagline: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />

              {/* Icon Upload */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>Icon</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file, 'icon', true);
                    }}
                    id="subcategory-icon-upload"
                  />
                  {subcategoryFormState.icon && (
                    <IconButton
                      size="small"
                      onClick={() => openImageInNewTab(subcategoryFormState.icon)}
                      sx={{ '&:hover': { background: '#e8f5e8' } }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {subcategoryFormState.icon && (
                  <TextField
                    label="Icon URL"
                    value={subcategoryFormState.icon}
                    onChange={e => setSubcategoryFormState((s: any) => ({ ...s, icon: e.target.value }))}
                    fullWidth
                    size="small"
                  />
                )}
                {uploadingImage === 'icon' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption">Uploading... {uploadProgress}%</Typography>
                    <Box sx={{ flex: 1, height: 4, background: '#e2e8f0', borderRadius: 2 }}>
                      <Box sx={{ height: '100%', background: '#3b82f6', borderRadius: 2, width: `${uploadProgress}%` }} />
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Header Image Upload */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>Header Image</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file, 'headerImageUrl', true);
                    }}
                    id="subcategory-header-upload"
                  />
                  {subcategoryFormState.headerImageUrl && (
                    <IconButton
                      size="small"
                      onClick={() => openImageInNewTab(subcategoryFormState.headerImageUrl)}
                      sx={{ '&:hover': { background: '#e8f5e8' } }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {subcategoryFormState.headerImageUrl && (
                  <TextField
                    label="Header Image URL"
                    value={subcategoryFormState.headerImageUrl}
                    onChange={e => setSubcategoryFormState((s: any) => ({ ...s, headerImageUrl: e.target.value }))}
                    fullWidth
                    size="small"
                  />
                )}
                {uploadingImage === 'headerImageUrl' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption">Uploading... {uploadProgress}%</Typography>
                    <Box sx={{ flex: 1, height: 4, background: '#e2e8f0', borderRadius: 2 }}>
                      <Box sx={{ height: '100%', background: '#3b82f6', borderRadius: 2, width: `${uploadProgress}%` }} />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        }
        open={showSubcategoryModal}
        onCloseHandler={cancelSubcategoryEdit}
        title="Subcategory Form"
        width="70%"
      />
    </div>
  );
};

const DataRow = ({ label, value, isUrl = false, onCopy, onView }: {
  label: string,
  value: any,
  isUrl?: boolean,
  onCopy?: (text: string, fieldName: string) => void,
  onView?: (url: string) => void
}) => (
  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
    <Typography variant="caption" color="text.secondary">{label}:</Typography>
    <Typography
      variant="body2"
      fontWeight={600}
      sx={{
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      }}
    >
      {String(value)}
    </Typography>
    {onCopy && (
      <IconButton
        size="small"
        onClick={() => onCopy(String(value), label)}
        sx={{
          padding: '2px',
          '&:hover': { background: '#e3f2fd' }
        }}
      >
        <ContentCopy fontSize="small" />
      </IconButton>
    )}
    {isUrl && value && onView && (
      <IconButton
        size="small"
        onClick={() => onView(String(value))}
        sx={{
          padding: '2px',
          '&:hover': { background: '#e8f5e8' }
        }}
      >
        <Visibility fontSize="small" />
      </IconButton>
    )}
  </Box>
);

export default AdminCategoriesPage;
